import { apiClient, setAuthToken, clearAuthToken, getStoredToken } from './api'
import { adminSocketService } from './socketService'
import {
  MappingService,
  ApiErrorHandler,
  FrontendOrderStatus,
  FrontendUserRole,
  CreateFlowerVariantRequest,
  UpdateFlowerVariantRequest,
  CreateBouquetRequest,
  UpdateBouquetRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  ReorderCategoriesRequest,
  ReorderCategoriesResponse,
  FlowerVariantFilters,
  BouquetFilters,
  ChatFilters,
  MessageFilters,
  GetNotificationsParams,
  GetNotificationsResponse,
  MarkNotificationReadResponse,
  MarkAllNotificationsReadResponse,
  DeleteNotificationResponse,
  FloristPendingOrdersResponse,
  FloristConfirmationsResponse,
  API_ENDPOINTS,
} from '@tenant/shared'

/**
 * Helper to build full API URL
 * @internal
 */
const buildUrl = (endpoint: string): string => {
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
  return `${API_BASE_URL}${endpoint}`
}

interface UpdateOrderStatusOptions {
  notes?: string | undefined
  photo?: string | undefined
  currentStatus?: FrontendOrderStatus | undefined
  userRole?: FrontendUserRole | undefined
  userId?: string | undefined
}

export interface CancelAndRefundResponse {
  success: boolean
  duplicate?: boolean
  order: {
    id: string
    status: string
    orderNumber?: string
    totalAmount?: number
    subtotal?: number
    deliveryFee?: number
  }
  payment: {
    action: 'cancelled' | 'refunded' | 'none' | 'already_handled'
    refundId: string | null
    refundConfirmation: {
      type: string
      confirmData: string | null
    } | null
    amount: number | null
  }
}

interface GetOrdersParams {
  status?: string | string[]
  search?: string
  dateFrom?: string
  dateTo?: string
  floristId?: string
  courierId?: string
  customerId?: string
  page?: number
  limit?: number
}

export class AdminApiService {
  static async login(email: string, password: string) {
    try {
      const response = await apiClient.auth.login({ email, password })
      setAuthToken(response.token)
      return response.user
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Avtorizasiya xətası')
    }
  }

  static logout() {
    clearAuthToken()
    // Çıxışda WebSocket bağlantısını kəsirik
    adminSocketService.disconnect()
  }

  static async getCurrentUser() {
    try {
      const response = await apiClient.auth.me()
      return response.user
    } catch (error) {
      clearAuthToken()
      throw error
    }
  }

  static async getOrders(filters: GetOrdersParams = {}) {
    try {
      return await apiClient.admin.getOrders(filters)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Sifarişlərin yüklənməsi xətası')
    }
  }

  /**
   * Change order status with FSM validation
   *
   * @param orderId - Order ID
   * @param newStatus - Target status to transition to
   * @param options - Additional options (notes, photo, validation context)
   * @returns Updated order
   * @throws Error if transition is not allowed by FSM
   */
  static async changeOrderStatus(
    orderId: string,
    newStatus: FrontendOrderStatus,
    options: UpdateOrderStatusOptions = {}
  ) {
    const { notes, photo, currentStatus, userRole } = options

    if (currentStatus && userRole) {
      const isAllowed = MappingService.canChangeStatus(currentStatus, newStatus, userRole)
      if (!isAllowed) {
        throw new Error(
          `"${userRole}" rolu üçün statusu "${currentStatus}" statusundan "${newStatus}" statusuna dəyişmək mümkün deyil`
        )
      }
    }

    try {
      return await apiClient.admin.updateOrderStatus(orderId, newStatus, notes, photo)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Statusun yenilənməsi xətası')
    }
  }

  // ============= EXPLICIT ORDER STATUS METHODS =============
  // Following Fowler's "Change Function Declaration" principle
  // Split generic changeOrderStatus into explicit, self-documenting methods

  /**
   * Mark order as preparing (florist starts work)
   * Transition: confirmed → preparing
   *
   * @param orderId - Order ID
   * @param context - Validation context (currentStatus, userRole)
   * @returns Updated order
   */
  static async startPreparation(
    orderId: string,
    context: { currentStatus?: FrontendOrderStatus; userRole?: FrontendUserRole } = {}
  ) {
    return this.changeOrderStatus(orderId, 'preparing', context)
  }

  /**
   * Mark order as ready (bouquet completed)
   * Transition: preparing → ready
   * Requires: photo
   *
   * @param orderId - Order ID
   * @param photo - Photo URL of completed bouquet (required)
   * @param context - Additional context (notes, currentStatus, userRole)
   * @returns Updated order
   */
  static async markAsReady(
    orderId: string,
    photo: string,
    context: { notes?: string; currentStatus?: FrontendOrderStatus; userRole?: FrontendUserRole } = {}
  ) {
    return this.changeOrderStatus(orderId, 'ready', { ...context, photo })
  }

  /**
   * Start delivery (courier picks up order)
   * Transition: ready → in-transit
   *
   * @param orderId - Order ID
   * @param context - Validation context (currentStatus, userRole)
   * @returns Updated order
   */
  static async startDelivery(
    orderId: string,
    context: { currentStatus?: FrontendOrderStatus; userRole?: FrontendUserRole } = {}
  ) {
    return this.changeOrderStatus(orderId, 'in-transit', context)
  }

  /**
   * Complete delivery (order delivered to customer)
   * Transition: in-transit → delivered
   * Requires: photo
   *
   * @param orderId - Order ID
   * @param photo - Proof of delivery photo (required)
   * @param context - Additional context (notes, currentStatus, userRole)
   * @returns Updated order
   */
  static async completeDelivery(
    orderId: string,
    photo: string,
    context: { notes?: string; currentStatus?: FrontendOrderStatus; userRole?: FrontendUserRole } = {}
  ) {
    return this.changeOrderStatus(orderId, 'delivered', { ...context, photo })
  }

  /**
   * Cancel order with reason
   * Transition: any → cancelled
   * Requires: reason
   *
   * @param orderId - Order ID
   * @param reason - Cancellation reason (required)
   * @param context - Validation context (currentStatus, userRole)
   * @returns Updated order
   */
  static async cancelOrder(
    orderId: string,
    reason: string,
    context: { currentStatus?: FrontendOrderStatus; userRole?: FrontendUserRole } = {}
  ) {
    return this.changeOrderStatus(orderId, 'cancelled', { ...context, notes: reason })
  }

  /**
   * Cancel order and handle payment in a single atomic operation.
   * Process Manager pattern (EIP): automatically cancels pending payment or creates refund.
   *
   * @param orderId - Order ID
   * @param reason - Cancellation reason (required)
   * @returns Order + payment action result
   */
  static async cancelAndRefundOrder(
    orderId: string,
    reason: string,
    options?: { skipPayment?: boolean }
  ): Promise<CancelAndRefundResponse> {
    try {
      const token = getStoredToken()

      const response = await fetch(
        buildUrl(API_ENDPOINTS.orders.command.cancelAndRefund(orderId)),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ reason, ...(options?.skipPayment && { skipPayment: true }) }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        const message = errorData.error || `HTTP ${response.status}`
        const detail = errorData.detail || null
        const err = new Error(detail ? `${message}\n${detail}` : message)
        ;(err as any).statusCode = response.status
        ;(err as any).detail = detail
        throw err
      }

      return await response.json()
    } catch (error) {
      if (error instanceof Error) {
        // Preserve statusCode and detail from the original error
        const wrapped = new Error(error.message)
        ;(wrapped as any).statusCode = (error as any).statusCode
        ;(wrapped as any).detail = (error as any).detail
        throw wrapped
      }
      throw new Error('Sifarişin ləğvi və geri ödəmə xətası')
    }
  }

  // ============= ASSIGNMENT METHODS =============

  /**
   * Assign executor (florist or courier) to order
   *
   * @param orderId - Order ID
   * @param executor - Executor to assign (either floristId or courierId)
   * @returns Updated order
   */
  static async assignExecutorToOrder(
    orderId: string,
    executor: { floristId?: string; courierId?: string }
  ) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.orders.canonical.assign(orderId)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(executor)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'İcraçının təyin edilməsi xətası')
    }
  }

  /**
   * Floristin təyinatını ləğv et (CONFIRMED → PENDING)
   * Yalnız ADMIN üçün əlçatandır
   */
  static async unassignFlorist(orderId: string, reason?: string) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.orders.command.unassignFlorist(orderId)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ reason })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Florist təyinatının ləğvi xətası')
    }
  }

  /**
   * Florist və ya kuryerin bir neçə sifarişə kütləvi təyin edilməsi
   * ADMIN və MANAGER-FLORIST (floristlər), DELIVERY-MANAGER (kuryerlər) üçün əlçatandır
   *
   * @param orderIds - Sifariş ID-lərinin massivi
   * @param floristId - Təyin ediləcək floristin ID-si (courierId ilə qarşılıqlı istisnadır)
   * @param courierId - Təyin ediləcək kuryerin ID-si (floristId ilə qarşılıqlı istisnadır)
   * @returns Uğurlu və uğursuz əməliyyatların təfərrüatları ilə kütləvi təyinat nəticələri
   */
  static async bulkAssignOrders(data: {
    orderIds: string[]
    floristId?: string
    courierId?: string
  }): Promise<{
    success: boolean
    message: string
    results: {
      total: number
      successful: number
      failed: number
      successfulOrderIds: string[]
      failedOrders: Array<{ orderId: string; error: string }>
    }
  }> {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.orders.canonical.bulkAssign), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kütləvi təyinat xətası')
    }
  }

  /**
   * Sync payment status from BirPay API
   * Used to manually verify/reconcile payments that may have missed webhooks
   */
  static async syncPaymentStatus(orderId: string): Promise<{
    success: boolean
    data: {
      birpay: { status: string; paid: boolean; paymentMethod: string | null; cancelationReason: string | null }
      local: { paymentStatus: string; orderStatus: string | null; paidAt: string | null; synced: boolean }
      orderDeleted?: boolean
    }
  }> {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.orders.command.syncPayment(orderId)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Ödəniş statusunu sinxronlaşdırma xətası')
    }
  }

  /**
   * Florist/admin confirms manual cash payment received at the store.
   * Creates a Payment row with paymentMethod='cash', status='SUCCEEDED'.
   */
  static async confirmCashPayment(orderId: string): Promise<{ success: boolean; paymentId: string }> {
    const token = getStoredToken()
    const response = await fetch(buildUrl(API_ENDPOINTS.orders.command.confirmCashPayment(orderId)), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }
    return await response.json()
  }

  /**
   * Admin-only: cancel a previously confirmed cash payment.
   */
  static async cancelCashPayment(
    orderId: string,
    paymentId: string,
    reason?: string,
  ): Promise<{ success: boolean; paymentId: string }> {
    const token = getStoredToken()
    const response = await fetch(buildUrl(API_ENDPOINTS.orders.command.cancelCashPayment(orderId)), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ paymentId, reason }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }
    return await response.json()
  }

  static async getOrderTracking(orderId: string) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.orders.query.tracking(orderId)), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Sifariş izləmə məlumatının alınması xətası')
    }
  }

  static async getUsers(filters = {}) {
    try {
      return await apiClient.admin.getUsers(filters)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'İstifadəçilərin yüklənməsi xətası')
    }
  }

  static async createUser(data: any) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.users.create), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'İstifadəçi yaratma xətası')
    }
  }

  static async updateUser(id: string, data: any) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.users.update(id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'İstifadəçi yeniləmə xətası')
    }
  }

  static async deactivateUser(id: string) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.users.delete(id)), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'İstifadəçinin deaktivasiya xətası')
    }
  }

  static async getStats() {
    try {
      return await apiClient.admin.getStats()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Statistikanın yüklənməsi xətası')
    }
  }

  static async getNavigationCounts(): Promise<Record<string, number>> {
    try {
      const response = await apiClient.admin.getNavigationCounts()
      return response?.data ?? {}
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Naviqasiya sayğaclarının yüklənməsi xətası')
    }
  }

  // ============= ÇİÇƏKLƏRİN İDARƏ EDİLMƏSİ =============
  static async getFlowers(filters: FlowerVariantFilters = {}) {
    try {
      return await apiClient.admin.flowers.getAll(filters)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çiçəklərin yüklənməsi xətası')
    }
  }

  static async getFlowerById(id: string) {
    try {
      return await apiClient.admin.flowers.getById(id)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çiçəyin yüklənməsi xətası')
    }
  }

  static async createFlower(flowerData: CreateFlowerVariantRequest) {
    try {
      return await apiClient.admin.flowers.create(flowerData)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çiçək yaratma xətası')
    }
  }

  static async updateFlower(id: string, flowerData: UpdateFlowerVariantRequest) {
    try {
      return await apiClient.admin.flowers.update(id, flowerData)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çiçək yeniləmə xətası')
    }
  }

  static async deleteFlower(id: string) {
    try {
      return await apiClient.admin.flowers.delete(id)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çiçək silmə xətası')
    }
  }

  static async updateFlowerAvailability(id: string, isAvailable: boolean) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.catalog.flowers.availability(id)), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ isAvailable })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çiçək mövcudluğunun yenilənməsi xətası')
    }
  }

  static async bulkUpdateFlowers(ids: string[], updates: Partial<UpdateFlowerVariantRequest>) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.catalog.flowers.bulk), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ ids, updates })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çiçəklərin kütləvi yenilənməsi xətası')
    }
  }

  // ============= BUKETLƏRİN İDARƏ EDİLMƏSİ =============
  static async getBouquets(filters: BouquetFilters = {}) {
    try {
      return await apiClient.admin.bouquets.getAll(filters)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Buketlərin yüklənməsi xətası')
    }
  }

  static async getBouquetById(id: string) {
    try {
      return await apiClient.admin.bouquets.getById(id)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Buketin yüklənməsi xətası')
    }
  }

  static async createBouquet(bouquetData: CreateBouquetRequest) {
    try {
      return await apiClient.admin.bouquets.create(bouquetData)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Buket yaratma xətası')
    }
  }

  static async updateBouquet(id: string, bouquetData: UpdateBouquetRequest) {
    try {
      return await apiClient.admin.bouquets.update(id, bouquetData)
    } catch (error: any) {
      // ✅ Preserve original error with response.data.details for slug conflict handling
      if (error?.response?.data?.details) {
        throw error // Re-throw original error with all metadata
      }
      throw new Error(error instanceof Error ? error.message : 'Buket yeniləmə xətası')
    }
  }

  static async deleteBouquet(id: string) {
    try {
      return await apiClient.admin.bouquets.delete(id)
    } catch (error: any) {
      // Preserve original error with response.data for proper error handling upstream
      if (error?.response) {
        throw error
      }
      throw new Error(error instanceof Error ? error.message : 'Buket silmə xətası')
    }
  }

  // ============= KATEQORİYALARIN İDARƏ EDİLMƏSİ =============
  static async getCategories() {
    try {
      return await apiClient.admin.categories.getAll()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriyaların yüklənməsi xətası')
    }
  }

  static async getCategoryById(id: string) {
    try {
      return await apiClient.admin.categories.getById(id)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriyanın yüklənməsi xətası')
    }
  }

  static async createCategory(categoryData: CreateCategoryRequest) {
    try {
      return await apiClient.admin.categories.create(categoryData)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriya yaratma xətası')
    }
  }

  static async updateCategory(id: string, categoryData: UpdateCategoryRequest) {
    try {
      return await apiClient.admin.categories.update(id, categoryData)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriya yeniləmə xətası')
    }
  }

  static async deleteCategory(id: string) {
    try {
      return await apiClient.admin.categories.delete(id)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriya silmə xətası')
    }
  }

  static async deleteCategoryWithAction(
    id: string,
    action: 'reassign' | 'delete',
    targetCategoryId?: string
  ) {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.catalog.categories.forceDelete(id)), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ action, targetCategoryId })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }

      return true
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriya silmə xətası')
    }
  }

  static async reorderCategories(data: ReorderCategoriesRequest): Promise<ReorderCategoriesResponse> {
    try {
      const token = getStoredToken()

      const response = await fetch(buildUrl(API_ENDPOINTS.catalog.categories.reorder), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }))
        throw new Error(errorData.message || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriyaların sırasının dəyişdirilməsi xətası')
    }
  }

  static async getCategoryBouquets(categoryId: string) {
    try {
      return await apiClient.admin.categories.getBouquets(categoryId)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriya buketlərinin yüklənməsi xətası')
    }
  }

  static async updateCategoryBouquets(categoryId: string, bouquetIds: string[]) {
    try {
      return await apiClient.admin.categories.updateBouquets(categoryId, bouquetIds)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Kateqoriya buketlərinin yenilənməsi xətası')
    }
  }

  // ============= ÇATLARIN İDARƏ EDİLMƏSİ =============
  static async getChats(filters: ChatFilters = {}) {
    try {
      return await apiClient.admin.chats.getAll(filters)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çatların yüklənməsi xətası')
    }
  }

  static async getChatById(id: string) {
    try {
      return await apiClient.admin.chats.getById(id)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çatın yüklənməsi xətası')
    }
  }

  static async getChatMessages(chatId: string, params: MessageFilters = {}) {
    try {
      return await apiClient.admin.chats.getMessages(chatId, params)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Mesajların yüklənməsi xətası')
    }
  }

  static async sendChatMessage(
    chatId: string,
    content: string,
    type: 'text' | 'image' | 'file' = 'text'
  ) {
    try {
      return await apiClient.admin.chats.sendMessage(chatId, content, type)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Mesaj göndərmə xətası')
    }
  }

  static async updateChatStatus(
    chatId: string,
    status: 'open' | 'closed' | 'pending',
    options: { supportAgentId?: string | null; slaDueAt?: string | Date | null } = {}
  ) {
    try {
      const payload = {
        status,
        supportAgentId: options.supportAgentId ?? null,
        slaDueAt: options.slaDueAt ? new Date(options.slaDueAt).toISOString() : null,
      }

      return await apiClient.admin.chats.updateStatus(chatId, payload)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Çat statusunun yenilənməsi xətası')
    }
  }

  static async markChatAsRead(chatId: string) {
    try {
      return await apiClient.admin.chats.markAsRead(chatId)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Oxunma statusunun yenilənməsi xətası')
    }
  }

  // 🎫 TICKET SYSTEM METHODS

  static async assignTicket(chatId: string, agentId: string) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/chats/${chatId}/assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ agentId }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Tiketin təyin edilməsi xətası')
    }
  }

  static async closeTicket(chatId: string, resolution: string, closedReason?: string) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/chats/${chatId}/close`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ resolution, closedReason }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Tiketin bağlanması xətası')
    }
  }

  static async reopenTicket(chatId: string) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/chats/${chatId}/reopen`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Tiketin yenidən açılması xətası')
    }
  }

  static async setTicketPriority(
    chatId: string,
    priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'
  ) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/chats/${chatId}/priority`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ priority }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Tiket prioritetinin dəyişdirilməsi xətası')
    }
  }

  static async setTicketCategory(
    chatId: string,
    category: string
  ) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/chats/${chatId}/category`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ category }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Tiket kateqoriyasının dəyişdirilməsi xətası')
    }
  }

  static async getTicketStatistics(
    agentId?: string,
    dateRange?: { from: Date; to: Date }
  ) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const params = new URLSearchParams()
      if (agentId) params.append('agentId', agentId)
      if (dateRange?.from) params.append('fromDate', dateRange.from.toISOString())
      if (dateRange?.to) params.append('toDate', dateRange.to.toISOString())

      const response = await fetch(
        `${API_BASE_URL}/admin/chats/tickets/statistics${params.toString() ? `?${params}` : ''}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const result = await response.json()
      return result.data
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Tiket statistikasının yüklənməsi xətası')
    }
  }

  // ============= BİLDİRİŞLƏRİN İDARƏ EDİLMƏSİ =============

  /**
   * Filtrasiya və səhifələmə ilə bildirişlərin siyahısını əldə et
   */
  static async getNotifications(params: GetNotificationsParams = {}): Promise<GetNotificationsResponse> {
    try {
      return await apiClient.admin.notifications.getAll(params)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Bildirişlərin yüklənməsi xətası')
    }
  }

  /**
   * Bildirişi oxunmuş kimi işarələ
   */
  static async markNotificationAsRead(notificationId: string): Promise<MarkNotificationReadResponse> {
    try {
      return await apiClient.admin.notifications.markAsRead(notificationId)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Bildirişin yenilənməsi xətası')
    }
  }

  /**
   * Bütün bildirişləri oxunmuş kimi işarələ
   */
  static async markAllNotificationsAsRead(category?: string): Promise<MarkAllNotificationsReadResponse> {
    try {
      return await apiClient.admin.notifications.markAllAsRead(category as any)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Bildirişlərin yenilənməsi xətası')
    }
  }

  /**
   * Bildirişi sil
   */
  static async deleteNotification(notificationId: string): Promise<DeleteNotificationResponse> {
    try {
      return await apiClient.admin.notifications.delete(notificationId)
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Bildirişin silinməsi xətası')
    }
  }

  // ============= FLORİSTLƏRİN İŞ AXINI =============

  /**
   * Təyin edilmiş floristi olmayan sifarişləri əldə et
   * @param params - Filtrlər (storeId, page, limit)
   */
  static async getFloristPendingOrders(params?: {
    storeId?: string | undefined
    page?: number | undefined
    limit?: number | undefined
  }): Promise<FloristPendingOrdersResponse> {
    try {
      return await apiClient.get<FloristPendingOrdersResponse>(
        '/admin/florist/pending-orders',
        { params }
      )
    } catch (error) {
      ApiErrorHandler.handle(error, {
        endpoint: '/admin/florist/pending-orders',
        method: 'GET',
        params
      })
    }
  }

  /**
   * Filtrlərlə floristlərin təsdiqləmələrini əldə et
   * @param params - Filtrlər (floristId, status, storeId, page, limit)
   */
  static async getFloristConfirmations(params?: {
    floristId?: string | undefined
    status?: 'pending' | 'accepted' | 'completed' | 'declined' | undefined
    storeId?: string | undefined
    page?: number | undefined
    limit?: number | undefined
  }): Promise<FloristConfirmationsResponse> {
    try {
      return await apiClient.get<FloristConfirmationsResponse>(
        '/admin/florist/confirmations',
        { params }
      )
    } catch (error) {
      ApiErrorHandler.handle(error, {
        endpoint: '/admin/florist/confirmations',
        method: 'GET',
        params
      })
    }
  }

  // ============= SÜRƏTLİ CAVABLAR (QUICK REPLIES) =============

  /**
   * Bütün sürətli cavab şablonlarını əldə et
   */
  static async getQuickReplies() {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/quick-replies`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` })
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const result = await response.json()
      return result.quickReplies
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Şablonların yüklənməsi xətası')
    }
  }

  /**
   * Yeni sürətli cavab şablonu yarat
   */
  static async createQuickReply(text: string) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/quick-replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ text })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const result = await response.json()
      return result.quickReply
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Şablon yaratma xətası')
    }
  }

  /**
   * Sürətli cavab şablonunu yenilə
   */
  static async updateQuickReply(id: string, data: { text?: string; order?: number }) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/quick-replies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const result = await response.json()
      return result.quickReply
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Şablon yeniləmə xətası')
    }
  }

  /**
   * Sürətli cavab şablonunu sil
   */
  static async deleteQuickReply(id: string) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/quick-replies/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` })
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      return true
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Şablon silmə xətası')
    }
  }

  /**
   * Sürətli cavab şablonlarının sırasını dəyişdir
   */
  static async reorderQuickReplies(orderedIds: string[]) {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const token = getStoredToken()

      const response = await fetch(`${API_BASE_URL}/admin/quick-replies/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ orderedIds })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const result = await response.json()
      return result.quickReplies
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Sıranın dəyişdirilməsi xətası')
    }
  }

}

// ✅ PHASE 1: Removed MappingService export - use `import { MappingService } from '@tenant/shared'` instead
export { apiClient, setAuthToken, clearAuthToken }
